import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { HomepageTakeover } from '@propertypal/shared/src/types/homepageTakeover';
import { Property } from '@propertypal/shared/src/types/property';
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import ImageSlideshow from '@propertypal/web-ui/src/animation/ImageSlideshow';
import FeaturesTrio from '@propertypal/web-ui/src/home/FeaturesTrio';
import PopularPropertiesHome from '@propertypal/web-ui/src/home/PopularPropertiesHome';
import RecentPropertiesHome from '@propertypal/web-ui/src/home/RecentPropertiesHome';
import MortgageIntroJumboTron from '@propertypal/web-ui/src/jumbotrons/MortgageIntroJumboTron';
import Takeover from '@propertypal/web-ui/src/takeover/Takeover';
import pick from 'lodash/pick';
import Head from 'next/head';
import { GetServerSideProps } from 'next/types';
import React, { FunctionComponent } from 'react';
import MetaTags, { DEFAULT_TITLE } from '../components/head/MetaTags';
import HomeSearchBar from '../components/search/HomeSearchBar';
import featuresTrioData from '../constants/homepage/featuresTrioData';
import cachedRequest from '../services/cachedRequest';
import { getCfIpCountry, requireAuthToken } from '../utils/auth';

const POPULAR_COUNT = 4;

interface Props {
  superfeatures: SuperFeatureProperty[];
  recent: Property[];
  popular: Property[];
  cfIpCountry: string | null;
  takeover: HomepageTakeover | null;
}

const simplifyProperty = (property: Property) => {
  return pick(property, [
    'account',
    'images',
    'path',
    'id',
    'displayAddress',
    'price',
    'epc',
    'ber',
    'logos',
    'addressLine1',
    'town',
    'listingLogos',
    'alternativeCurrencyPrice',
    'listingTime',
    'agents',
  ]);
};

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx) => {
  const result = await axios(
    {
      method: 'GET',
      url: `${API_URL}/homepage`,
    },
    undefined,
    ctx,
  );

  const superfeatures = result.data.superfeatures.sort(() => Math.random() - 0.5);

  const popular = result.data.popular
    .filter((item: Property) => !item.premiumListing)
    .sort(() => Math.random() - 0.5)
    .slice(0, POPULAR_COUNT)
    .map(simplifyProperty);

  const siteMapLinks =
    getCfIpCountry(ctx) === 'IE'
      ? await cachedRequest('/site-map/footer/irl', ctx, 6)
      : await cachedRequest('/site-map/footer/nir', ctx, 6);

  let takeover: Props['takeover'] = null;

  if (result.data.takeover) {
    // simplify listing property from takeover in result
    takeover = {
      ...result.data.takeover,
      // Any changes here should be reflected in the HomepageTakeover["listing"] type
      listing: pick(result.data.takeover.listing, [
        'listingLogos',
        'path',
        'price',
        'showHomeOpeningTime',
        'images',
        'displayAddress',
        'displayAddressLine1',
      ]),
    };
  }

  return {
    props: {
      superfeatures,
      recent: result.data.recent.map(simplifyProperty),
      popular,
      siteMapLinks,
      takeover,
    },
  };
});

const Home: FunctionComponent<Props> = (props) => {
  const images = props.superfeatures.map((row) => row.url || row.image.url);
  const highResImages = props.superfeatures.map((row) => row.url || row.image?.urls['1600x1067:FILL_CROP']);

  return (
    <>
      <Head>
        <title>{DEFAULT_TITLE}</title>

        <MetaTags url="/" />
      </Head>

      {props.takeover && props.takeover.listing ? (
        <Takeover takeover={props.takeover}>
          <HomeSearchBar searchUrl="/search" baseParams="pt=residential&st=sale" showRent submitText="For Sale" />
        </Takeover>
      ) : (
        <ImageSlideshow
          images={images || []}
          highResImages={highResImages}
          slideDuration={10000}
          superFeatureProperties={props.superfeatures}
          highResThreshold={1600}
        >
          <HomeSearchBar
            searchUrl="/search"
            baseParams="pt=residential&st=sale"
            showRent
            submitText="For Sale"
            // showInstantValuation={props.cfIpCountry !== 'IE'}
          />
        </ImageSlideshow>
      )}

      {/* {props.cfIpCountry !== 'IE' && <ExploreBar valuation={props.valuation} />} */}

      <PopularPropertiesHome
        title="Popular Properties"
        properties={props.popular}
        link="/property-for-sale/northern-ireland/sort-hot"
        linkText="View all Popular Properties"
        imageLocator="526x386:FILL_CROP"
        maxLength={POPULAR_COUNT}
        maxColumns={POPULAR_COUNT}
      />

      {props.cfIpCountry !== 'IE' && <MortgageIntroJumboTron />}

      <FeaturesTrio heading="House hunting is easier with PropertyPal" data={featuresTrioData} />

      {props.recent && <RecentPropertiesHome properties={props.recent.slice(0, 18)} />}
    </>
  );
};

export default Home;
